import { GateLiveStatusOutcome } from '@/__generated__/graphql';
import { NetworkStatus, useQuery } from '@apollo/client';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GET_GATE_LIVE_STATUS } from '../graphql/queries/getGateLiveStatus';

/** Cada límite es del firmware si lo reportó; `null` el que no, que el formulario completa con la base. */
export interface FirmwareLimits {
  min: number | null;
  max: number | null;
}

/** Códigos de `UNAVAILABLE` en que el equipo no contestó: se lo da por desconectado. */
const DEVICE_SILENT_CODES = new Set(['GATE_COMMAND_TIMEOUT']);

const isDeviceSilent = (status: { code?: string | null } | null) =>
  Boolean(status?.code && DEVICE_SILENT_CODES.has(status.code));

/**
 * Fuente de verdad del modal: posición, límites y movimiento salen de acá, y los datos de la base
 * sólo son el fallback cuando el equipo no da lectura (~1 s conectado, ~5 s hasta el timeout).
 * `network-only`: una lectura cacheada no es «en vivo»; `skip` hace que reabrir reconsulte.
 */
export const useGateLiveStatus = (gateId: string | null) => {
  const { data, error, networkStatus, refetch } = useQuery(GET_GATE_LIVE_STATUS, {
    variables: { gateId: gateId ?? '' },
    skip: !gateId,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  // `setVariables`: cambió la compuerta con el modal abierto; es una primera carga, no un refresco.
  const initialLoading =
    networkStatus === NetworkStatus.loading || networkStatus === NetworkStatus.setVariables;
  const refreshing = networkStatus === NetworkStatus.refetch;
  const loading = initialLoading || refreshing;
  // Un refetch en curso no muestra la lectura anterior: sería presentar como vigente un dato que se
  // acaba de pedir renovar.
  // Atado al `gateId`: nunca se muestra la lectura de otra compuerta.
  // Un refresco fallido deja el `data` anterior junto al error: no se presenta como vigente.
  const current = !error && data?.gate.id === gateId ? data?.gate.liveStatus ?? null : null;
  const liveStatus = loading ? null : current;

  const reading =
    liveStatus?.outcome === GateLiveStatusOutcome.Reading ? liveStatus.reading ?? null : null;

  // El formulario sí conserva la lectura previa durante un refresco: si se vaciara, el slider
  // saltaría a los límites de la base y volvería al llegar la respuesta.
  const lastStatus = current;
  const formReading =
    lastStatus?.outcome === GateLiveStatusOutcome.Reading ? lastStatus.reading ?? null : null;

  // Campo a campo: un límite que el equipo sí reportó siempre manda, aunque falte el otro.
  const firmwareLimits = useMemo<FirmwareLimits | null>(() => {
    const min = formReading?.minPosition ?? null;
    const max = formReading?.maxPosition ?? null;
    if (min === null && max === null) return null;
    if (min !== null && max !== null && min > max) return null;
    return { min, max };
  }, [formReading]);

  // Interlock conservador: un movimiento reportado sigue valiendo hasta que una lectura nueva diga
  // lo contrario — un refresco en curso o fallido no lo libera. Se reinicia al cerrar el modal o
  // cambiar de compuerta.
  const readingMoving = reading ? reading.moving === true : undefined;
  const [movingGateId, setMovingGateId] = useState<string | null>(null);
  useEffect(() => {
    if (!gateId) setMovingGateId(null);
    else if (readingMoving !== undefined) setMovingGateId(readingMoving ? gateId : null);
  }, [gateId, readingMoving]);
  const lastKnownMoving = readingMoving ?? (gateId !== null && movingGateId === gateId);

  const refresh = useCallback(() => {
    // El error ya lo expone el hook (`failed`); acá sólo se evita el rechazo no manejado.
    refetch().catch(() => {});
  }, [refetch]);

  return {
    liveStatus,
    // Mientras se espera al equipo el modal no muestra datos de la base como vigentes.
    awaiting: Boolean(gateId) && loading,
    /** Primera consulta de la apertura: el formulario todavía no tiene de dónde partir. */
    initialLoading: Boolean(gateId) && initialLoading,
    refreshing,
    reading,
    formReading,
    deviceSilent: isDeviceSilent(liveStatus),
    /** Como `deviceSilent`, pero conserva la última respuesta durante un refresco (para el envío). */
    lastDeviceSilent: isDeviceSilent(lastStatus),
    /** La compuerta se mueve según la última lectura del equipo; sólo la libera una lectura nueva. */
    lastKnownMoving,
    // Un error de GraphQL (permiso, red) se presenta igual que un `UNAVAILABLE`: el modal sigue.
    failed: Boolean(error) && !loading,
    loading,
    firmwareLimits,
    refresh,
  };
};

export type GateLiveStatusState = ReturnType<typeof useGateLiveStatus>;
